// ============================================
// moldClaw License Hook
// ============================================

/**
 * 🧑‍💻 SWE-3: 라이선스 상태 관리
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './useAuth';
import {
  License,
  Device,
  getUserLicenses,
  getDevices,
  removeDevice as apiRemoveDevice,
  createCheckoutSession,
  createPortalSession,
  ApiException,
} from '../lib/api';

interface LicenseContextType {
  // 라이선스 데이터
  licenses: License[];
  currentLicense: License | null;
  devices: Device[];
  
  // 로딩 상태
  isLoading: boolean;
  isDevicesLoading: boolean;
  
  // 에러
  error: string | null;
  
  // 액션
  refresh: () => Promise<void>;
  loadDevices: () => Promise<void>;
  removeDevice: (deviceId: string) => Promise<void>;
  checkout: (tier: 'pro' | 'team') => Promise<void>;
  manageSubscription: () => Promise<void>;
  
  // 유틸리티
  hasActiveLicense: boolean;
  canAddDevice: boolean;
  tierLabel: string;
}

const LicenseContext = createContext<LicenseContextType | undefined>(undefined);

export function LicenseProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  
  const [licenses, setLicenses] = useState<License[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDevicesLoading, setIsDevicesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 현재 활성 라이선스 (가장 높은 티어)
  const currentLicense = licenses.length > 0
    ? licenses.reduce((best, lic) => {
        if (!best) return lic;
        const tierRank = { free: 0, pro: 1, team: 2 };
        return tierRank[lic.tier] > tierRank[best.tier] ? lic : best;
      }, licenses[0])
    : null;
  
  // 라이선스 데이터 새로고침
  const refresh = useCallback(async () => {
    if (!user) {
      setLicenses([]);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getUserLicenses();
      setLicenses(data);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.message);
      } else {
        setError('라이선스 정보를 불러오는데 실패했습니다');
      }
      setLicenses([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);
  
  // 기기 목록 로드
  const loadDevices = useCallback(async () => {
    if (!currentLicense) {
      setDevices([]);
      return;
    }
    
    setIsDevicesLoading(true);
    
    try {
      const data = await getDevices();
      setDevices(data);
    } catch (err) {
      console.error('Failed to load devices:', err);
      setDevices([]);
    } finally {
      setIsDevicesLoading(false);
    }
  }, [currentLicense]);
  
  // 기기 제거
  const removeDevice = useCallback(async (deviceId: string) => {
    try {
      await apiRemoveDevice(deviceId);
      setDevices(prev => prev.filter(d => d.id !== deviceId));
    } catch (err) {
      if (err instanceof ApiException) {
        throw new Error(err.message);
      }
      throw new Error('기기 제거에 실패했습니다');
    }
  }, []);
  
  // Stripe Checkout으로 이동
  const checkout = useCallback(async (tier: 'pro' | 'team') => {
    try {
      const { url } = await createCheckoutSession(tier);
      window.location.href = url;
    } catch (err) {
      if (err instanceof ApiException) {
        throw new Error(err.message);
      }
      throw new Error('결제 페이지 생성에 실패했습니다');
    }
  }, []);
  
  // Stripe Customer Portal로 이동
  const manageSubscription = useCallback(async () => {
    try {
      const { url } = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      if (err instanceof ApiException) {
        throw new Error(err.message);
      }
      throw new Error('구독 관리 페이지 접근에 실패했습니다');
    }
  }, []);
  
  // 사용자 변경 시 라이선스 로드
  useEffect(() => {
    refresh();
  }, [user?.id, refresh]);
  
  // 계산된 값들
  const hasActiveLicense = currentLicense?.status === 'active';
  const canAddDevice = currentLicense 
    ? currentLicense.devices_used < currentLicense.max_devices 
    : false;
  
  const tierLabel = currentLicense 
    ? { free: 'Free', pro: 'Pro', team: 'Team' }[currentLicense.tier]
    : 'None';
  
  return (
    <LicenseContext.Provider
      value={{
        licenses,
        currentLicense,
        devices,
        isLoading,
        isDevicesLoading,
        error,
        refresh,
        loadDevices,
        removeDevice,
        checkout,
        manageSubscription,
        hasActiveLicense,
        canAddDevice,
        tierLabel,
      }}
    >
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense() {
  const context = useContext(LicenseContext);
  if (context === undefined) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
}

// ============================================
// Tier 정보 상수 (가격 페이지용)
// ============================================

export const TIER_INFO = {
  free: {
    name: 'Free',
    price: 0,
    priceLabel: '무료',
    period: '',
    description: '입문자를 위한 무료 체험',
    features: [
      '기기 1대 등록',
      '기본 기능 사용',
      '커뮤니티 지원',
    ],
    limitations: [
      '상업적 사용 불가',
      '고급 기능 제한',
    ],
    cta: '무료로 시작',
    popular: false,
  },
  pro: {
    name: 'Pro',
    price: 9,
    priceLabel: '$9',
    period: '/월',
    description: '개인 개발자를 위한 플랜',
    features: [
      '기기 3대 등록',
      '모든 기능 사용',
      '우선 지원',
      '상업적 사용 가능',
      '자동 업데이트',
    ],
    limitations: [],
    cta: '구독하기',
    popular: true,
  },
  team: {
    name: 'Team',
    price: 29,
    priceLabel: '$29',
    period: '/월',
    description: '팀 협업을 위한 플랜',
    features: [
      '기기 5대 등록',
      '모든 Pro 기능 포함',
      '팀 관리 기능',
      '전용 지원',
      '사용량 분석',
      'API 접근',
    ],
    limitations: [],
    cta: '팀 시작',
    popular: false,
  },
} as const;
